import React from "react";
import classNames from "classnames";
import type { CheckboxProps } from "./types";
import styles from "./Checkbox.module.css";

/**
 * Checkbox component with support for checked, indeterminate, and radio modes.
 * Follows the Vue Checkbox.vue component design patterns.
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked = false,
      indeterminate = false,
      label,
      disabled = false,
      size = "md",
      mode = "checkbox",
      variant = "default",
      onChange,
      "data-testid": testId,
      ...props
    },
    ref,
  ) => {
    const handleClick = () => {
      if (!disabled && onChange) {
        onChange(!checked);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick();
      }
    };

    // Determine the icon to display based on state
    const getIcon = () => {
      if (checked) {
        return (
          <svg
            className={classNames(styles.icon, styles.checked)}
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
        );
      }

      if (indeterminate) {
        return (
          <svg
            className={classNames(styles.icon, styles.indeterminate)}
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z" />
          </svg>
        );
      }

      // Unchecked state - circle outline
      return (
        <svg
          className={classNames(styles.icon, styles.unchecked)}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
        </svg>
      );
    };

    return (
      <div
        className={classNames(styles.container, {
          [styles.disabled]: disabled,
          [styles.radio]: mode === "radio",
          [styles.dark]: variant === "dark",
        })}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={mode === "radio" ? "radio" : "checkbox"}
        aria-checked={indeterminate ? "mixed" : checked}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        data-size={size}
        data-testid={testId}
      >
        {/* Hidden native input for form compatibility - role="none" removes semantic role while allowing label focus */}
        <input
          ref={ref}
          type={mode === "radio" ? "radio" : "checkbox"}
          checked={checked}
          disabled={disabled}
          onChange={() => {}} // Controlled by click handler
          className={styles.hiddenInput}
          tabIndex={-1}
          role="none"
          {...props}
        />

        {getIcon()}

        {label && <span className={styles.label}>{label}</span>}
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
